// Spec 48 ai-ux — agent_run_visualizer.
//
// "Vertical timeline of an agent run's plan steps with per-step status,
// duration, trace preview, and link-through to approval cards for
// waiting_approval steps." (spec 48 line 1019)
//
// a11y per spec 48:
//   - Timeline is <ol> with aria-label="Agent run plan"
//   - Each step is <li> with aria-current="step" on the executing node
//   - Waiting_approval link opens the approval card with focus transfer
//   - Failed steps use state_danger; text-labeled and icon-labeled
//   - Screen reader announces status changes via aria-live on the
//     step_status_chip

import {
  forwardRef,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";

export type AgentStepStatus =
  | "pending"
  | "planning"
  | "executing"
  | "waiting_approval"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface AgentRunStep {
  readonly id: string;
  readonly label: string;
  readonly status: AgentStepStatus;
  /** "ran in 1.2s" */
  readonly durationText?: string;
  /** Trace preview content shown on expand. */
  readonly tracePreview?: ReactNode;
  /** When status === "waiting_approval", clicking opens the approval card. */
  readonly onReviewApproval?: () => void;
  /** When status === "failed" + step is retryable. */
  readonly onRetry?: () => void;
  /** Optional failure reason ("Failed — {reason}"). */
  readonly failureReason?: string;
}

export interface AgentRunVisualizerProps extends HTMLAttributes<HTMLOListElement> {
  /** Agent run header text — e.g., "Offboarding Orchestrator • run arun_4012 • triggered by Jamie". */
  readonly runHeader?: string;
  readonly steps: readonly AgentRunStep[];
  /** Override timeline aria-label (defaults to "Agent run plan"). */
  readonly timelineAriaLabel?: string;
  // i18n labels:
  readonly statusLabels?: Partial<Record<AgentStepStatus, string>>;
  readonly reviewApprovalLabel?: string;
  readonly retryLabel?: string;
  readonly viewTraceLabel?: string;
  readonly hideTraceLabel?: string;
}

const DEFAULT_STATUS_LABELS: Readonly<Record<AgentStepStatus, string>> = {
  pending: "Pending",
  planning: "Planning",
  executing: "Running",
  waiting_approval: "Waiting on approval",
  succeeded: "Done",
  failed: "Failed",
  cancelled: "Cancelled",
};

export const AgentRunVisualizer = forwardRef<HTMLOListElement, AgentRunVisualizerProps>(
  (
    {
      runHeader,
      steps,
      timelineAriaLabel = "Agent run plan",
      statusLabels,
      reviewApprovalLabel = "Review approval",
      retryLabel = "Retry step",
      viewTraceLabel = "View trace",
      hideTraceLabel = "Hide trace",
      className,
      ...rest
    },
    ref,
  ) => {
    const resolvedLabels = { ...DEFAULT_STATUS_LABELS, ...(statusLabels ?? {}) };

    return (
      <div data-component="agent-run-visualizer-wrapper" className="nai-agent-run-visualizer-wrapper">
        {runHeader !== undefined && (
          <div
            data-component="agent-run-visualizer-header"
            className="nai-agent-run-visualizer-header"
          >
            {runHeader}
          </div>
        )}
        <ol
          ref={ref}
          aria-label={timelineAriaLabel}
          data-component="agent-run-visualizer"
          className={cx("nai-agent-run-visualizer", className)}
          {...rest}
        >
          {steps.map((step) => (
            <AgentRunStepItem
              key={step.id}
              step={step}
              statusLabel={resolvedLabels[step.status]}
              reviewApprovalLabel={reviewApprovalLabel}
              retryLabel={retryLabel}
              viewTraceLabel={viewTraceLabel}
              hideTraceLabel={hideTraceLabel}
            />
          ))}
        </ol>
      </div>
    );
  },
);
AgentRunVisualizer.displayName = "AgentRunVisualizer";

// ==========================================================================
// STEP ITEM (extracted to keep useState local per row)
// ==========================================================================

interface AgentRunStepItemProps {
  readonly step: AgentRunStep;
  readonly statusLabel: string;
  readonly reviewApprovalLabel: string;
  readonly retryLabel: string;
  readonly viewTraceLabel: string;
  readonly hideTraceLabel: string;
}

function AgentRunStepItem({
  step,
  statusLabel,
  reviewApprovalLabel,
  retryLabel,
  viewTraceLabel,
  hideTraceLabel,
}: AgentRunStepItemProps): ReactNode {
  const [traceOpen, setTraceOpen] = useState(false);
  const isCurrent = step.status === "executing" || step.status === "planning";

  return (
    <li
      data-component="agent-run-step"
      data-status={step.status}
      aria-current={isCurrent ? "step" : undefined}
      className={cx("nai-agent-run-step", `nai-agent-run-step--${step.status}`)}
    >
      <span
        aria-hidden="true"
        data-component="agent-run-step-node"
        className="nai-agent-run-step-node"
      />
      <div className="nai-agent-run-step-content">
        <div className="nai-agent-run-step-headline">
          <span data-component="agent-run-step-label" className="nai-agent-run-step-label">
            {step.label}
          </span>
          <span
            aria-live="polite"
            data-component="agent-run-step-status-chip"
            className="nai-agent-run-step-status-chip"
          >
            {step.status === "failed" && step.failureReason !== undefined
              ? `${statusLabel} — ${step.failureReason}`
              : statusLabel}
          </span>
          {step.durationText !== undefined && (
            <span
              data-component="agent-run-step-duration"
              className="nai-agent-run-step-duration"
            >
              {step.durationText}
            </span>
          )}
        </div>
        {step.tracePreview !== undefined && (
          <>
            <button
              type="button"
              data-component="agent-run-step-trace-toggle"
              className="nai-agent-run-step-trace-toggle"
              aria-expanded={traceOpen}
              onClick={() => setTraceOpen((o) => !o)}
            >
              {traceOpen ? hideTraceLabel : viewTraceLabel}
            </button>
            {traceOpen && (
              <div
                data-component="agent-run-step-trace"
                className="nai-agent-run-step-trace"
                aria-label="Step trace"
              >
                {step.tracePreview}
              </div>
            )}
          </>
        )}
        {step.status === "waiting_approval" && step.onReviewApproval !== undefined && (
          <button
            type="button"
            data-component="agent-run-step-review-approval"
            className="nai-agent-run-step-review-approval"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              step.onReviewApproval?.();
            }}
          >
            {reviewApprovalLabel}
          </button>
        )}
        {step.status === "failed" && step.onRetry !== undefined && (
          <button
            type="button"
            data-component="agent-run-step-retry"
            className="nai-agent-run-step-retry"
            onClick={() => step.onRetry?.()}
          >
            {retryLabel}
          </button>
        )}
      </div>
    </li>
  );
}
